import React, { useCallback, useEffect, useRef, useState } from 'react';

import { CurrencyCodeType } from '@/types/currency';
import { JackpotBetAmountsType } from '@/types/jackpotTypes';
import {
  formatNumberToCurrency,
  isPrefixCurrencySymbol,
  mapCurrencyToSymbol,
} from '@/utils/currency';

import styles from './JackpotBetWidget.module.scss';

export interface JackpotBetWidgetTypeProps {
  amounts: JackpotBetAmountsType[];
  prizeLabels: Record<string, string>;
  currencyCode?: CurrencyCodeType;
}

const ANIMATION_TIME = 300;

const JackpotBetWidgetType = ({
  amounts,
  prizeLabels,
  currencyCode,
}: JackpotBetWidgetTypeProps) => {
  const formatJackpotAmount = useCallback(
    (amount: number) => formatNumberToCurrency(amount, currencyCode),
    [currencyCode]
  );

  const jpBetAmounts = amounts.map((jpAmount: JackpotBetAmountsType) => ({
    type: jpAmount.type,
    label: prizeLabels[jpAmount.type],
    value: `${
      formatNumberToCurrency(jpAmount.amount, currencyCode).split('.')[0]
    }`,
  }));

  const [animate, setAnimate] = useState(false);
  const [currentName, setCurrentName] = useState(jpBetAmounts[0].label);
  const [currentValue, setCurrentValue] = useState(jpBetAmounts[0].value);
  const [currentDecimalValue, setCurrentDecimalValue] = useState(
    formatJackpotAmount(amounts[0].amount).split('.')[1] || '00'
  );
  const decimalValue = animate ? `.00` : `.${currentDecimalValue}`;

  const index = useRef(0);
  const currency = useRef(currencyCode);

  useEffect(() => {
    if (currency.current !== currencyCode) {
      setCurrentName(jpBetAmounts[index.current].label);
      setCurrentValue(jpBetAmounts[index.current].value);
      setCurrentDecimalValue(
        formatJackpotAmount(amounts[index.current].amount).split('.')[1] || '00'
      );
      currency.current = currencyCode;
    }

    const interval = setInterval(() => {
      setAnimate(true);

      const newIndex = (index.current + 1) % jpBetAmounts.length;

      setTimeout(() => {
        setCurrentName(jpBetAmounts[newIndex].label);
        setCurrentValue(jpBetAmounts[newIndex].value);
        setCurrentDecimalValue(
          formatJackpotAmount(amounts[newIndex].amount).split('.')[1] || '00'
        );
        index.current = newIndex;
        setAnimate(false);
      }, ANIMATION_TIME);
    }, 4000);

    return () => clearInterval(interval);
  }, [amounts, jpBetAmounts, currencyCode, formatJackpotAmount]);

  return (
    <div
      className={[
        styles.jackpotType,
        animate ? null : styles.jackpotTypeShadow,
      ].join(' ')}
      data-testid="jackpot-bet-types"
    >
      <div
        className={[
          styles.jackpotName,
          animate ? styles.hidden : styles.visible,
        ].join(' ')}
      >
        {currentName}
      </div>
      <div className={styles.jackpotValueContainer}>
        <div className={styles.jackpotValueAnimated}>
          <span
            className={[
              styles.jackpotValue,
              animate ? styles.exit : styles.enter,
            ].join(' ')}
          >
            {' '}
            {`${
              isPrefixCurrencySymbol(currencyCode)
                ? mapCurrencyToSymbol(currencyCode)
                : ''
            }${currentValue}`}
          </span>
          <span
            className={[
              styles.jackpotValueZeroAnimation,
              animate ? styles.visible : '',
            ].join(' ')}
          >
            00
          </span>
        </div>
        <div>
          <span className={styles.jackpotValueDecimals}>
            {`${decimalValue} ${
              !isPrefixCurrencySymbol(currencyCode)
                ? mapCurrencyToSymbol(currencyCode)
                : ''
            }`}
          </span>
        </div>
      </div>
    </div>
  );
};

export default JackpotBetWidgetType;
